import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

/** Data passed in when opening the dialog: the current value + a callback applied on every change. */
export interface ValidityPeriodDialogData {
  /** The form's current validity period in days; `null`/`undefined` ⇒ unbegrenzt gültig. */
  validityPeriodDays?: number | null;
  /** Called with the new value whenever it changes (immediate-apply, like the required-reviews dialog). */
  onChange: (validityPeriodDays: number | null) => void;
}

/** The presets offered as one-tap choices, in display order. Anything else goes in the free number input. */
export const VALIDITY_PRESET_DAYS: number[] = [90, 180, 365, 730];

/**
 * Form-builder dialog for the form's **Gültigkeitsdauer** — how long a completed submission stays valid before it
 * counts as expired (and is then treated exactly like a review flag: 14 days of grace, then a lockout). Opened via
 * MatDialog for the same reason the required-reviews dialog is: the CDK overlay renders at the document root, above
 * the builder's split-view panels, where a plain in-canvas popup was trapped under the preview column.
 *
 * The dialog applies every change immediately through {@link ValidityPeriodDialogData.onChange} — there is no OK /
 * Cancel, matching the rest of the builder where edits land on the definition as they are made.
 */
@Component({
  selector: 'forms-validity-period-popup',
  standalone: true,
  imports: [],
  template: `
    <div class="flex flex-col" role="dialog" aria-labelledby="validity-period-title">
      <div class="border-b border-gray-100 pb-3">
        <h2 id="validity-period-title" class="text-xl font-bold tracking-tight text-gray-900">Gültigkeitsdauer</h2>
        <!-- gray-500/600 rather than gray-400 throughout: gray-400 measures 2.4:1 against the dialog surface,
             far under the 4.5:1 floor. Pinned by the axe check in this component's spec. -->
        <p class="mt-1 text-sm text-gray-500">
          Wie lange eine ausgefüllte Einreichung gültig bleibt. Danach gilt sie als abgelaufen und muss erneuert
          werden.
        </p>
      </div>

      <div class="flex flex-col gap-2 py-4">
        <label class="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-100 p-3 transition-colors hover:bg-gray-50">
          <input type="radio" name="validityMode" [checked]="!limited" (change)="setUnlimited()"
                 class="mt-0.5 h-4 w-4 border-gray-300 text-theme-primary-600 focus:ring-theme-primary-500"/>
          <span class="flex flex-col">
            <span class="text-sm font-semibold text-gray-900">Unbegrenzt gültig</span>
            <span class="text-xs text-gray-600">Eine Einreichung läuft nie ab.</span>
          </span>
        </label>

        <label class="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-100 p-3 transition-colors hover:bg-gray-50">
          <input type="radio" name="validityMode" [checked]="limited" (change)="setLimited()"
                 class="mt-0.5 h-4 w-4 border-gray-300 text-theme-primary-600 focus:ring-theme-primary-500"/>
          <span class="flex flex-col">
            <span class="text-sm font-semibold text-gray-900">Nach einer bestimmten Zeit erneuern</span>
            <span class="text-xs text-gray-600">Nach Ablauf wird der Mitarbeiter zur Erneuerung aufgefordert.</span>
          </span>
        </label>

        @if (limited) {
          <div class="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3">
            <div class="flex flex-wrap gap-2">
              @for (preset of presets; track preset) {
                <button type="button" (click)="apply(preset)"
                        [attr.aria-pressed]="days === preset"
                        class="rounded-full border px-3 py-1 text-xs font-semibold transition-colors"
                        [class.border-theme-primary-300]="days === preset"
                        [class.bg-theme-primary-50]="days === preset"
                        [class.text-theme-primary-700]="days === preset"
                        [class.border-gray-200]="days !== preset"
                        [class.text-gray-600]="days !== preset">{{ preset }} Tage</button>
              }
            </div>
            <div class="flex items-center gap-2">
              <label for="validity-days" class="text-sm font-bold tracking-tight text-gray-700">Gültig für</label>
              <input id="validity-days" type="number" min="1" step="1" [value]="days ?? ''"
                     (input)="onDaysInput($event)"
                     class="w-28 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500"/>
              <span class="text-sm text-gray-500">Tage</span>
            </div>
          </div>
        }
      </div>

      <div class="flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
        <p class="text-xs text-gray-500">{{ summary }}</p>
        <button type="button" (click)="done()"
                class="inline-flex justify-center rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900 focus:outline-none focus:ring-2 focus:ring-theme-primary-500 focus:ring-offset-2">
          Fertig
        </button>
      </div>
    </div>
  `
})
export class ValidityPeriodPopupComponent {
  protected readonly presets = VALIDITY_PRESET_DAYS;

  /** The current value in days; `null` while the form is unbegrenzt gültig. */
  protected days: number | null;
  /** Whether the "renew after N days" mode is selected. Kept separate so an emptied input does not flip the mode. */
  protected limited: boolean;

  constructor(
    private ref: MatDialogRef<ValidityPeriodPopupComponent>,
    @Inject(MAT_DIALOG_DATA) private data: ValidityPeriodDialogData
  ) {
    const initial = data?.validityPeriodDays ?? null;
    this.days = initial && initial > 0 ? initial : null;
    this.limited = this.days !== null;
  }

  /** The plain-language answer to "what does this form do now?", restated under the controls. */
  protected get summary(): string {
    return this.days === null
      ? 'Einreichungen sind unbegrenzt gültig.'
      : `Einreichungen sind ${this.days} Tage gültig.`;
  }

  /** Switch to "unbegrenzt": clears the value and publishes `null`. */
  protected setUnlimited(): void {
    this.limited = false;
    this.apply(null);
  }

  /**
   * Switch to the limited mode. Publishing only happens once there is a usable number, so merely selecting the
   * radio never writes a half-typed value onto the definition.
   */
  protected setLimited(): void {
    this.limited = true;
    if (this.days !== null) this.apply(this.days);
  }

  /** Handle the free number input: an empty or non-positive value means "nothing chosen yet", not "unbegrenzt". */
  protected onDaysInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.trim();
    if (raw === '') {
      this.apply(null, false);
      return;
    }
    const parsed = Math.floor(Number(raw));
    this.apply(Number.isFinite(parsed) && parsed > 0 ? parsed : null, false);
  }

  /** Set the value and publish it to the definition. */
  protected apply(days: number | null, syncMode = true): void {
    this.days = days;
    if (syncMode) this.limited = days !== null;
    this.data.onChange(days);
  }

  protected done(): void {
    this.ref.close();
  }
}
